import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Users, CheckCircle, XCircle, Loader2, AlertTriangle } from 'lucide-react';
import { Button, Card } from '@/components/ui';
import { useAuthStore } from '@/stores';
import { useToast } from '@/components/ui/Toast';
import { acceptInvitation, getUserProfile, getInvitationPreview } from '@perfin/shared';

type InviteStatus = 'loading' | 'ready' | 'accepting' | 'accepted' | 'error' | 'invalid';

interface InvitationDetails {
  email: string;
  orgName: string;
  role: string;
  inviterName: string | null;
}

export function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuthStore();
  const toast = useToast();

  const [status, setStatus] = useState<InviteStatus>('loading');
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [acceptedOrgName, setAcceptedOrgName] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Fetch invitation details on mount
  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }

    const fetchInvitation = async () => {
      try {
        const details = await getInvitationPreview(token);
        setInvitation(details);
        setStatus('ready');
      } catch (error: any) {
        console.error('Failed to fetch invitation:', error);
        setErrorMessage(error?.message || 'Invalid or expired invitation');
        setStatus('invalid');
      }
    };

    fetchInvitation();
  }, [token]);

  // Check if user's email matches invitation
  const emailMatches = user && invitation
    ? user.email?.toLowerCase() === invitation.email.toLowerCase()
    : true; // Default to true if we can't check

  // Accept the invitation
  const handleAccept = async () => {
    if (!user || !token) return;

    setStatus('accepting');
    setErrorMessage(null);
    try {
      // Force token refresh to ensure latest claims (especially mfaVerified after MFA enrollment)
      await user.getIdToken(true);

      const { orgName } = await acceptInvitation(token, user.uid);
      setAcceptedOrgName(orgName);
      setStatus('accepted');
      toast.success(`Welcome to ${orgName}!`);

      // CRITICAL: Refresh token and profile to get the new organizationId
      // The Cloud Function sets custom claims and updates the user doc,
      // but the client still has stale data. Without this refresh,
      // AppLayout will think the user has no org and create a new one.
      await user.getIdToken(true); // Force refresh to get new custom claims
      const updatedProfile = await getUserProfile(user.uid);
      useAuthStore.setState({ profile: updatedProfile });

      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error: any) {
      setStatus('error');
      // Extract message from Firebase HttpsError
      const message = error?.message || 'Failed to accept invitation';
      setErrorMessage(message);
      toast.error(message);
    }
  };

  // No token provided or invalid invitation
  if (status === 'invalid' || !token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="max-w-md w-full text-center py-12">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Invitation</h1>
          <p className="text-gray-600 mb-8">{errorMessage || 'This invitation link is invalid or has expired.'}</p>
          <p className="text-sm text-gray-500">Contact the invitation provider about this error.</p>
        </Card>
      </div>
    );
  }

  // Loading invitation details or auth state
  if (status === 'loading' || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-primary-500 animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Loading invitation...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="max-w-md w-full text-center py-12">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Unable to Accept</h1>
          <p className="text-gray-600 mb-8">{errorMessage}</p>
          <div className="space-y-3">
            <Button onClick={() => setStatus('ready')} variant="secondary" className="w-full">
              Try Again
            </Button>
            <Link to="/login">
              <Button variant="secondary" className="w-full">Go to Login</Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  // Render accepted state
  if (status === 'accepted') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="max-w-md w-full text-center py-12">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome!</h1>
          <p className="text-gray-600 mb-8">
            You've successfully joined {acceptedOrgName || 'the organization'}. Redirecting to dashboard...
          </p>
          <Loader2 className="h-6 w-6 text-primary-500 animate-spin mx-auto" />
        </Card>
      </div>
    );
  }

  // User needs to sign in first - show invitation details
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="max-w-md w-full text-center py-12">
          <Users className="h-16 w-16 text-primary-500 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">You're Invited!</h1>

          {invitation && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Organization:</span> {invitation.orgName}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Invited as:</span> {invitation.role}
                </p>
                {invitation.inviterName && (
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Invited by:</span> {invitation.inviterName}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-amber-800">
              <span className="font-medium">Important:</span> Please sign in or create an account using{' '}
              <span className="font-semibold">{invitation?.email}</span>
            </p>
          </div>

          <div className="space-y-3">
            <Link to={`/login?redirect=/invite/${token}&email=${encodeURIComponent(invitation?.email || '')}`} className="block">
              <Button className="w-full">Sign In</Button>
            </Link>
            <Link to={`/signup?redirect=/invite/${token}&email=${encodeURIComponent(invitation?.email || '')}`} className="block">
              <Button variant="secondary" className="w-full">
                Create Account
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  // Ready to accept - show accept UI with invitation details
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="max-w-md w-full text-center py-12">
        <Users className="h-16 w-16 text-primary-500 mx-auto mb-6" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Accept Invitation</h1>

        {invitation && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Organization:</span> {invitation.orgName}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Role:</span> {invitation.role}
              </p>
              {invitation.inviterName && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Invited by:</span> {invitation.inviterName}
                </p>
              )}
              <p className="text-sm text-gray-600">
                <span className="font-medium">Invited email:</span> {invitation.email}
              </p>
            </div>
          </div>
        )}

        {!emailMatches && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-left">
              <p className="text-sm font-medium text-amber-800">Email Mismatch</p>
              <p className="text-sm text-amber-700 mt-1">
                You're signed in as <span className="font-medium">{user.email}</span>, but this invitation was sent to{' '}
                <span className="font-medium">{invitation?.email}</span>.
              </p>
              <p className="text-sm text-amber-700 mt-2">
                Please sign out and sign in with the correct email, or contact the person who invited you.
              </p>
            </div>
          </div>
        )}

        <p className="text-sm text-gray-500 mb-6">
          Signed in as <span className="font-medium">{user.email}</span>
        </p>

        <div className="space-y-3">
          <Button
            onClick={handleAccept}
            loading={status === 'accepting'}
            disabled={!emailMatches}
            className="w-full"
          >
            Accept Invitation
          </Button>
          <Link to="/">
            <Button variant="secondary" className="w-full">
              Decline
            </Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}
